import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  Divider,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Breadcrumbs,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Support as SupportIcon,
  Home as HomeIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Note as NoteIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../../utils/config';
import { format } from 'date-fns';

interface User {
  id: string;
  name: string;
  email: string;
  company?: string;
}

interface Message {
  id: number;
  content: string;
  sender: {
    id: string;
    name: string;
    email: string;
    type: 'user' | 'admin';
  };
  isInternalNote: boolean;
  createdAt: string;
  editedAt: string | null;
}

interface Thread {
  id: number;
  subject: string;
  category: string | null;
  status: string;
  priority: string;
  createdBy: User;
  assignedTo: {
    id: string;
    name: string;
  } | null;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
}

interface Participant {
  id: string;
  name: string;
  email: string;
  type: string;
}

interface ThreadResponse {
  thread: Thread;
  messages: Message[];
  participants: Participant[];
}

interface AdminUser {
  id: string;
  name: string;
  email: string;
}

const AdminMessageThread: React.FC = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { authenticatedFetch } = useAuthenticatedFetch();
  
  // Get the return URL from search params, default to support tickets list
  const returnUrl = searchParams.get('returnUrl') || '/admin/support-tickets';
  
  const [replyContent, setReplyContent] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditingMetadata, setIsEditingMetadata] = useState(false);
  const [editedStatus, setEditedStatus] = useState('');
  const [editedPriority, setEditedPriority] = useState('');
  const [editedCategory, setEditedCategory] = useState('');
  const [editedAssignedTo, setEditedAssignedTo] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Fetch thread details and messages
  const { data: threadData, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'messages', 'thread', threadId],
    queryFn: async () => {
      if (!threadId) throw new Error('Thread ID is required');

      const response = await authenticatedFetch(
        `${API_URL}/api/marketcompass/admin/messages/threads/${threadId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response?.ok) {
        if (response?.status === 404) {
          throw new Error('Thread not found');
        }
        throw new Error('Failed to fetch thread');
      }

      const result = await response.json();
      return result.data as ThreadResponse;
    },
    refetchInterval: 20000, // Refresh every 20 seconds
    staleTime: 0, // Always consider data stale
    gcTime: 0, // Don't cache the data
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  // Force immediate refresh when component mounts or threadId changes
  useEffect(() => {
    if (threadId) {
      // Clear any existing cache for this thread first
      queryClient.invalidateQueries({ queryKey: ['admin', 'messages', 'thread', threadId] });
      // Then fetch fresh data
      refetch();
    }
  }, [threadId, refetch, queryClient]);

  // Fetch admin users for assignment
  const { data: adminUsers } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const response = await authenticatedFetch(
        `${API_URL}/api/marketcompass/admin/messages/stats`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response?.ok) {
        throw new Error('Failed to fetch admin users');
      }

      const result = await response.json();
      return (result.data?.adminUsers || []) as AdminUser[];
    },
  });

  // Initialize edit form when thread loads
  useEffect(() => {
    if (threadData?.thread) {
      setEditedStatus(threadData.thread.status);
      setEditedPriority(threadData.thread.priority);
      setEditedCategory(threadData.thread.category || 'general');
      setEditedAssignedTo(threadData.thread.assignedTo?.id || '');
    }
  }, [threadData]);

  // Admin reply mutation
  const sendReplyMutation = useMutation({
    mutationFn: async (data: { content: string; isInternalNote: boolean; changeStatus?: string }) => {
      const response = await authenticatedFetch(
        `${API_URL}/api/marketcompass/admin/messages/thread-reply/${threadId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        }
      );

      if (!response?.ok) {
        throw new Error('Failed to send reply');
      }

      return response.json();
    },
    onSuccess: () => {
      setReplyContent('');
      setIsInternalNote(false);
      // Refetch thread messages
      queryClient.invalidateQueries({ queryKey: ['admin', 'messages', 'thread', threadId] });
      refetch(); // Force immediate refetch
      // Update thread list
      queryClient.invalidateQueries({ queryKey: ['admin', 'messages', 'threads'] });
    },
  });

  // Update thread metadata mutation
  const updateMetadataMutation = useMutation({
    mutationFn: async (updates: { status?: string; priority?: string; category?: string; assignedTo?: string }) => {
      const response = await authenticatedFetch(
        `${API_URL}/api/marketcompass/admin/messages/threads/${threadId}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updates),
        }
      );

      if (!response?.ok) {
        throw new Error('Failed to update thread');
      }

      return response.json();
    },
    onSuccess: () => {
      setIsEditingMetadata(false);
      // Refetch thread
      queryClient.invalidateQueries({ queryKey: ['admin', 'messages', 'thread', threadId] });
      refetch(); // Force immediate refetch
      // Update thread list
      queryClient.invalidateQueries({ queryKey: ['admin', 'messages', 'threads'] });
    },
  });

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [threadData?.messages]);

  const handleSendReply = async (forceStatus?: string) => {
    if (!replyContent.trim()) return;

    // Default to 'pending' for regular replies, unless forcing a specific status
    const statusToUse = forceStatus || (!isInternalNote ? 'pending' : undefined);

    setIsSubmitting(true);
    try {
      await sendReplyMutation.mutateAsync({
        content: replyContent,
        isInternalNote,
        changeStatus: statusToUse,
      });
      // Navigate back to support tickets list after successful send
      navigate(returnUrl);
    } catch (error) {
      console.error('Failed to send reply:', error);
      setIsSubmitting(false);
    }
  };

  const handleSendAndResolve = async () => {
    if (!replyContent.trim()) {
      // No content - just mark as resolved without sending a message
      setIsSubmitting(true);
      try {
        await updateMetadataMutation.mutateAsync({ status: 'resolved' });
        // Navigate back to support tickets list after successful status update
        navigate(returnUrl);
      } catch (error) {
        console.error('Failed to update status:', error);
        setIsSubmitting(false);
      }
    } else {
      // Has content - send reply and mark as resolved
      await handleSendReply('resolved');
    }
  };

  const handleCloseThread = async () => {
    setIsSubmitting(true);
    try {
      await updateMetadataMutation.mutateAsync({ status: 'closed' });
      // Navigate back to support tickets list after successfully closing
      navigate(returnUrl);
    } catch (error) {
      console.error('Failed to close thread:', error);
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey && e.shiftKey) {
      e.preventDefault();
      if (thread?.status === 'open' && !isInternalNote) {
        handleSendAndResolve();
      }
    } else if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      if (replyContent.trim()) {
        handleSendReply();
      }
    }
  };

  const handleSaveMetadata = async () => {
    const updates: any = {};
    
    if (editedStatus !== threadData?.thread.status) {
      updates.status = editedStatus;
    }
    if (editedPriority !== threadData?.thread.priority) {
      updates.priority = editedPriority;
    }
    if (editedCategory !== (threadData?.thread.category || 'general')) {
      updates.category = editedCategory;
    }
    if (editedAssignedTo !== (threadData?.thread.assignedTo?.id || '')) {
      updates.assignedTo = editedAssignedTo || null;
    }

    if (Object.keys(updates).length > 0) {
      await updateMetadataMutation.mutateAsync(updates);
    } else {
      setIsEditingMetadata(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingMetadata(false);
    if (threadData?.thread) {
      setEditedStatus(threadData.thread.status);
      setEditedPriority(threadData.thread.priority);
      setEditedCategory(threadData.thread.category || 'general');
      setEditedAssignedTo(threadData.thread.assignedTo?.id || '');
    }
  };

  const getStatusColor = (status: string): 'info' | 'warning' | 'success' | 'default' => {
    switch (status) {
      case 'open':
        return 'warning';
      case 'pending':
        return 'info';
      case 'resolved':
        return 'success';
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string): 'error' | 'warning' | 'primary' | 'default' => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'normal':
        return 'primary';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          {error instanceof Error && error.message === 'Thread not found' 
            ? 'This conversation could not be found.'
            : 'Failed to load conversation. Please try again later.'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(returnUrl)}
          sx={{ mt: 2 }}
        >
          Back to Support Tickets
        </Button>
      </Container>
    );
  }

  const thread = threadData?.thread;
  const messages = threadData?.messages || [];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/')}
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/admin/hub')}
          sx={{ cursor: 'pointer' }}
        >
          Admin Hub
        </Link>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate(returnUrl)}
          sx={{ cursor: 'pointer' }}
        >
          Support Tickets
        </Link>
        <Typography color="text.primary" variant="body2">
          Thread #{thread?.id}
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Main Content */}
        <Box sx={{ flexGrow: 1 }}>
          {/* Header */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={() => navigate(returnUrl)} edge="start">
                <ArrowBackIcon />
              </IconButton>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h5" gutterBottom>
                  {thread?.subject}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={thread?.status}
                    size="small"
                    color={getStatusColor(thread?.status || '')}
                  />
                  <Chip
                    label={thread?.priority}
                    size="small"
                    color={getPriorityColor(thread?.priority || '')}
                    variant={thread?.priority === 'normal' ? 'outlined' : 'filled'}
                  />
                  {thread?.category && (
                    <Chip
                      label={thread?.category}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {thread?.assignedTo && (
                    <Chip
                      icon={<AssignmentIcon />}
                      label={thread.assignedTo.name}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
            </Stack>
          </Paper>

          {/* Messages */}
          <Paper sx={{ mb: 3, p: 3, maxHeight: '60vh', overflow: 'auto' }}>
            {messages.length === 0 ? (
              <Typography color="text.secondary" textAlign="center">
                No messages yet
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {messages.map((message, index) => {
                  const isAdmin = message.sender.type === 'admin';
                  const isCustomer = !isAdmin;
                  const currentMessageDate = new Date(message.createdAt);
                  const previousMessageDate = index > 0 ? new Date(messages[index - 1].createdAt) : null;
                  
                  // Show date breaker when date changes or for first message
                  const showDateBreaker = 
                    index === 0 || 
                    (previousMessageDate && 
                      currentMessageDate.toDateString() !== previousMessageDate.toDateString());

                  return (
                    <Box key={message.id}>
                      {showDateBreaker && (
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ 
                            display: 'block', 
                            textAlign: 'center',
                            my: 2,
                            fontWeight: 500,
                          }}
                        >
                          {format(currentMessageDate, 'EEEE, MMMM d, yyyy')}
                        </Typography>
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: isAdmin ? 'flex-end' : 'flex-start',
                          mb: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: isAdmin ? 'row-reverse' : 'row',
                            alignItems: 'flex-end',
                            gap: 1,
                            maxWidth: '70%',
                          }}
                        >
                          {/* Avatar */}
                          <Avatar 
                            sx={{ 
                              width: 32, 
                              height: 32, 
                              bgcolor: isAdmin ? 'secondary.main' : 'primary.main',
                              flexShrink: 0,
                            }}
                          >
                            {isAdmin ? <SupportIcon sx={{ fontSize: 18 }} /> : <PersonIcon sx={{ fontSize: 18 }} />}
                          </Avatar>
                          
                          {/* Message Bubble */}
                          <Box>
                            {/* Internal Note Badge */}
                            {message.isInternalNote && (
                              <Chip
                                icon={<NoteIcon />}
                                label="Internal Note"
                                size="small"
                                color="warning"
                                sx={{ mb: 0.5, height: 20 }}
                              />
                            )}
                            
                            <Box
                              sx={{
                                position: 'relative',
                                px: 2,
                                py: 1.5,
                                borderRadius: 2.5,
                                backgroundColor: isAdmin 
                                  ? (message.isInternalNote ? '#fff3cd' : 'secondary.main')
                                  : '#f0f0f0',
                                color: isAdmin && !message.isInternalNote ? 'white' : 'text.primary',
                                // SMS-style bubble tail
                                '&::before': isAdmin ? {
                                  content: '""',
                                  position: 'absolute',
                                  right: -7,
                                  bottom: 10,
                                  width: 0,
                                  height: 0,
                                  borderLeft: `8px solid ${message.isInternalNote ? '#fff3cd' : 'var(--mui-palette-secondary-main)'}`,
                                  borderTop: '4px solid transparent',
                                  borderBottom: '4px solid transparent',
                                } : {
                                  content: '""',
                                  position: 'absolute',
                                  left: -7,
                                  bottom: 10,
                                  width: 0,
                                  height: 0,
                                  borderRight: '8px solid #f0f0f0',
                                  borderTop: '4px solid transparent',
                                  borderBottom: '4px solid transparent',
                                },
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ 
                                  display: 'block',
                                  mb: 0.5,
                                  fontWeight: 500,
                                  opacity: isAdmin && !message.isInternalNote ? 0.95 : 0.8,
                                }}
                              >
                                {message.sender.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  fontStyle: message.isInternalNote ? 'italic' : 'normal',
                                }}
                              >
                                {message.content}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  display: 'block',
                                  mt: 0.5,
                                  opacity: isAdmin && !message.isInternalNote ? 0.9 : 0.6,
                                  fontSize: '0.7rem',
                                }}
                              >
                                {format(new Date(message.createdAt), 'h:mm a')}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
                <div ref={messagesEndRef} />
              </Stack>
            )}
          </Paper>

          {/* Reply Box */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Admin Reply
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder={isInternalNote ? "Add an internal note (not visible to customer)..." : "Type your reply here..."}
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSubmitting}
              sx={{ mb: 2 }}
            />
            
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isInternalNote}
                      onChange={(e) => setIsInternalNote(e.target.checked)}
                      disabled={isSubmitting}
                    />
                  }
                  label="Internal Note"
                />
                
                <Box sx={{ flexGrow: 1 }} />
                
                <Button
                  variant="contained"
                  endIcon={<SendIcon />}
                  onClick={() => handleSendReply()}
                  disabled={!replyContent.trim() || isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Reply'}
                </Button>
                
                {thread?.status === 'open' && (
                  <Button
                    variant="contained"
                    color="success"
                    endIcon={<SendIcon />}
                    onClick={handleSendAndResolve}
                    disabled={isSubmitting || isInternalNote}
                    sx={{ ml: 1 }}
                  >
                    {isSubmitting ? 'Processing...' : (replyContent.trim() ? 'Send & Resolve' : 'Mark Resolved')}
                  </Button>
                )}
                
                {thread?.status === 'resolved' && (
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={<CheckCircleIcon />}
                    onClick={handleCloseThread}
                    disabled={isSubmitting}
                    sx={{ ml: 1 }}
                  >
                    {isSubmitting ? 'Closing...' : 'Close'}
                  </Button>
                )}
              </Stack>
              
              <Typography variant="caption" color="text.secondary">
                Press Ctrl+Enter to send{thread?.status === 'open' && (replyContent.trim() ? ', Ctrl+Shift+Enter to send & resolve' : ', Ctrl+Shift+Enter to mark resolved')}
              </Typography>
            </Stack>
          </Paper>
        </Box>

        {/* Sidebar */}
        <Box sx={{ width: 350 }}>
          {/* User Info */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Customer Information
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1" gutterBottom>
                {thread?.createdBy.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1" gutterBottom>
                {thread?.createdBy.email}
              </Typography>
              {thread?.createdBy.company && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Company
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {thread.createdBy.company}
                  </Typography>
                </>
              )}
              <Typography variant="body2" color="text.secondary">
                Created
              </Typography>
              <Typography variant="body1">
                {thread?.createdAt && format(new Date(thread.createdAt), 'MMM d, yyyy h:mm a')}
              </Typography>
            </CardContent>
          </Card>

          {/* Thread Metadata */}
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">
                  Thread Details
                </Typography>
                {!isEditingMetadata ? (
                  <IconButton size="small" onClick={() => setIsEditingMetadata(true)}>
                    <EditIcon />
                  </IconButton>
                ) : (
                  <Stack direction="row" spacing={1}>
                    <IconButton size="small" onClick={handleSaveMetadata} color="primary">
                      <SaveIcon />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancelEdit}>
                      <CancelIcon />
                    </IconButton>
                  </Stack>
                )}
              </Stack>
              <Divider sx={{ my: 1 }} />
              
              {!isEditingMetadata ? (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={thread?.status}
                    size="small"
                    color={getStatusColor(thread?.status || '')}
                    sx={{ mb: 1 }}
                  />
                  
                  <Typography variant="body2" color="text.secondary">
                    Priority
                  </Typography>
                  <Chip
                    label={thread?.priority}
                    size="small"
                    color={getPriorityColor(thread?.priority || '')}
                    sx={{ mb: 1 }}
                  />
                  
                  <Typography variant="body2" color="text.secondary">
                    Category
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {thread?.category || 'general'}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary">
                    Assigned To
                  </Typography>
                  <Typography variant="body1">
                    {thread?.assignedTo?.name || 'Unassigned'}
                  </Typography>
                </>
              ) : (
                <Stack spacing={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={editedStatus}
                      label="Status"
                      onChange={(e) => setEditedStatus(e.target.value)}
                    >
                      <MenuItem value="open">Open</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="resolved">Resolved</MenuItem>
                      <MenuItem value="closed">Closed</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl size="small" fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={editedPriority}
                      label="Priority"
                      onChange={(e) => setEditedPriority(e.target.value)}
                    >
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="urgent">Urgent</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl size="small" fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={editedCategory}
                      label="Category"
                      onChange={(e) => setEditedCategory(e.target.value)}
                    >
                      <MenuItem value="general">General</MenuItem>
                      <MenuItem value="support">Support</MenuItem>
                      <MenuItem value="billing">Billing</MenuItem>
                      <MenuItem value="technical">Technical</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl size="small" fullWidth>
                    <InputLabel>Assign To</InputLabel>
                    <Select
                      value={editedAssignedTo}
                      label="Assign To"
                      onChange={(e) => setEditedAssignedTo(e.target.value)}
                    >
                      <MenuItem value="">Unassigned</MenuItem>
                      {adminUsers?.map(admin => (
                        <MenuItem key={admin.id} value={admin.id}>
                          {admin.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Container>
  );
};

export default AdminMessageThread;